import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMediaQuery } from '@mui/material';
import { sheetsClient } from '../../data/sheetsClient';
import { useTeacherData } from '../../context/TeacherDataContext';
import { Card, Button, OutstandingBanner, TabBar, Skeleton, EmptyState, Modal, Picker } from '../shared/UI';
import Badge, { LockBadge } from '../shared/Badge';
import PaymentPendingNote from '../shared/PaymentPendingNote';
import { balanceOwingForDancer, paymentStatusForDancer, entriesForDancer, entryFeeTotalForDancer, entryRateFor, formatCurrency } from '../../utils/feeCalculations';
import { parseStudentCsv, downloadCsvTemplate } from '../../utils/csv';
import { paidForEntryAndParticipant, paidForParticipant, creditFor } from '../../../shared/utils/feePaymentLedger';
import { useTitleBarSearch } from '../../context/TitleBarSearchContext';

const TABS = ['Invite link', 'Roster', 'Groups'];
const PAYMENT_BADGE = {
  'fully-paid': { variant: 'success', label: 'Fully paid' },
  'participation-paid': { variant: 'warning', label: 'Participation fee paid' },
  'partially-paid': { variant: 'warning', label: 'Partially paid' },
  'not-paid': { variant: 'danger', label: 'Not paid' },
};

export default function Dancers() {
  const { school, dancers, entries, feeConfig, groups, locks, entryFeePayments, loading, refetch } = useTeacherData();
  const [activeTab, setActiveTab] = useState('Roster');
  const [selectedDancer, setSelectedDancer] = useState(null);
  const [sortCol, setSortCol] = useState('name'); // which column to sort by
  const [sortDir, setSortDir] = useState('asc'); // 'asc' or 'desc'
  const [filterReg, setFilterReg] = useState('all'); // 'all', 'not-started', 'missing-parent', 'complete'
  const [filterPay, setFilterPay] = useState('all'); // 'all', 'not-paid', 'participant-paid', 'partially-paid', 'fully-paid'
  const [search, setSearch] = useState('');
  const [editingGroup, setEditingGroup] = useState(null); // null = not editing, object = create/edit
  const isMobile = useMediaQuery('(max-width:768px)');

  // Below 768px the sidebar (and this search box's normal spot on the
  // Roster tab) isn't shown — relocate it into the mobile title bar
  // instead, only while it'd actually be visible (Roster tab, list view,
  // not the per-dancer detail view). No-op on desktop, where the inline
  // input further down is used as always.
  useTitleBarSearch(isMobile && !selectedDancer && activeTab === 'Roster' ? { placeholder: 'Search dancers…', value: search, onChange: setSearch } : null);

  // Once the roster loads, default straight to the invite link tab when
  // there's no one to show yet — but only that first time, so switching
  // tabs afterward (or filtering the roster down to zero) never yanks the
  // user back.
  const hasAutoSelectedTab = useRef(false);
  useEffect(() => {
    if (!hasAutoSelectedTab.current && dancers) {
      hasAutoSelectedTab.current = true;
      if (dancers.length === 0) setActiveTab('Invite link');
    }
  }, [dancers]);

  // The data-null case (loading finished but a fetch failed, e.g. an expired
  // session mid-redirect) is intentionally treated the same as still-loading
  // here rather than crashing on the .filter calls below.
  if (loading.dancers || loading.entries || loading.feeConfig || loading.groups || loading.entryFeePayments || !dancers || !entries || !feeConfig) {
    return (
      <div style={{ padding: 20, margin: '0 auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>Dancers</h1>
        <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>Click a dancer to view details and their full payment breakdown.</p>
        <Card title="Registered dancers">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #E5E5E5' }}>
              <Skeleton width="28%" height={14} />
              <Skeleton width="18%" height={20} radius={20} />
              <Skeleton width="18%" height={20} radius={20} />
              <Skeleton width="14%" height={14} style={{ marginLeft: 'auto' }} />
            </div>
          ))}
        </Card>
      </div>
    );
  }

  if (selectedDancer) {
    return (
      <DancerDetail
        dancer={selectedDancer}
        dancers={dancers}
        entries={entries}
        feeConfig={feeConfig}
        entryFeePayments={entryFeePayments}
        onBack={() => setSelectedDancer(null)}
        onSave={async (updated) => {
          await sheetsClient.saveDancer(updated);
          await refetch.dancers();
          setSelectedDancer(null);
        }}
        onUpdateParentEmail={async (dancerId, email) => {
          await sheetsClient.updateParentEmail(dancerId, email);
          setSelectedDancer(prev => ({ ...prev, parentGuardianEmail: email }));
          refetch.dancers();
        }}
        onResendParentEmail={async (dancerId) => {
          await sheetsClient.resendParentEmail(dancerId);
        }}
        onDelete={async () => {
          await sheetsClient.deleteDancer(selectedDancer.id);
          await refetch.dancers();
          setSelectedDancer(null);
        }}
        onReplace={async (newParticipantId, transferEntryTitles) => {
          await sheetsClient.replaceDancer(selectedDancer.id, newParticipantId, transferEntryTitles);
          await Promise.all([refetch.dancers(), refetch.entries(), refetch.entryFeePayments()]);
          setSelectedDancer(null);
        }}
      />
    );
  }

  //info for banner
  const missingConsent = dancers.filter((d) => d.studentSectionComplete && !d.parentSectionComplete);
  const notStarted = dancers.filter((d) => !d.studentSectionComplete);
  const dancersWithBalance = dancers.filter((d) => balanceOwingForDancer(d, entries, feeConfig, entryFeePayments) > 0);

  //sorting and filtering handlers
  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  //group create/edit/delete handlers
  const startNewGroup = () => setEditingGroup({ id: null, name: '', dancerIds: [] });

  const handleSaveGroup = async ({ id, name, dancerIds }) => {
    try {
      if (id) {
        await sheetsClient.saveGroup({ id, name, dancerIds });
      } else {
        await sheetsClient.createGroup({ name, dancerIds });
      }
      await refetch.groups();
      setEditingGroup(null);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteGroup = async () => {
    if (!window.confirm(`Delete "${editingGroup.name}"? This cannot be undone.`)) return;
    try {
      await sheetsClient.deleteGroup(editingGroup.id);
      await refetch.groups();
      setEditingGroup(null);
    } catch (err) {
      alert(err.message);
    }
  };

  const sortedDancers = [...dancers].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortCol === 'name') return dir * a.lastName.localeCompare(b.lastName);
    if (sortCol === 'balance') return dir * (balanceOwingForDancer(a, entries, feeConfig, entryFeePayments) - balanceOwingForDancer(b, entries, feeConfig, entryFeePayments));
    if (sortCol === 'registration') return dir * (a.parentSectionComplete ? 1 : -1);
    if (sortCol === 'payment') return dir * (paymentStatusForDancer(a, entries, feeConfig, entryFeePayments).localeCompare(paymentStatusForDancer(b, entries, feeConfig, entryFeePayments)));
    return 0;
  });

  const filteredDancers = sortedDancers.filter(d => {
    const regMatch =
      filterReg === 'all' ||
      (filterReg === 'not-started' && !d.studentSectionComplete) ||
      (filterReg === 'parent-pending' && d.studentSectionComplete && !d.parentSectionComplete) ||
      (filterReg === 'complete' && d.studentSectionComplete && d.parentSectionComplete);

    const paymentMatch =
      filterPay === 'all' ||
      filterPay === paymentStatusForDancer(d, entries, feeConfig, entryFeePayments);

    const nameMatch =
      !search.trim() ||
      `${d.firstName} ${d.lastName}`.toLowerCase().includes(search.trim().toLowerCase());

    return regMatch && paymentMatch && nameMatch;
  });


  return (
    <div style={{ padding: 20, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>Dancers</h1>
      <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>Click a dancer to view details and their full payment breakdown.</p>

      <PaymentPendingNote locks={locks} what="dancers" />

      <TabBar tabs={TABS} activeTab={activeTab} onChange={setActiveTab} counts={{ Roster: dancers.length }} />

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          {activeTab === 'Invite link' && (() => {
            const inviteLink = `${window.location.origin}${process.env.PUBLIC_URL}/registration/dancer?school=${encodeURIComponent(school.id)}`;
            // Same reasons as EntryLibrary's newEntriesBlocked — a waitlisted
            // school has no accepted seat yet; a withdrawn school had one but
            // isn't attending anymore; participantsLocked is the deadline/
            // invoice/estimate-driven lock (see participantsAtEstimate for
            // that last one specifically). All already reject the actual
            // writes server-side (see getSchoolBlockStatus/getEffectiveLocks
            // on POST /api/register/dancer and bulk-import) — this just
            // tells the teacher why before they try.
            const newDancersBlocked = locks?.participantsLocked || locks?.waitlisted || locks?.withdrawn || locks?.notRegistered;
            return (
              <>
                {newDancersBlocked && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                    <LockBadge locks={locks} />
                    <span style={{ fontSize: 13, color: '#666' }}>
                      {locks?.notRegistered
                        ? "New dancers can't be added yet — your school hasn't registered for this event. Register for the event first; this link won't accept sign-ups until then."
                        : locks?.waitlisted
                        ? "New dancers can't be added — your school is on the event waitlist. This link won't accept new sign-ups until you're promoted off the waitlist."
                        : locks?.withdrawn
                          ? "New dancers can't be added — your school has withdrawn from this event."
                          : locks?.participantsAtEstimate
                            ? "New dancers can't be added — you've reached the estimated number of participants you registered with. Contact OSSDF if you need to raise your estimate."
                            : 'New dancers can\'t be added — participant registration is closed for your school. Existing dancers can still be edited.'}
                    </span>
                  </div>
                )}
                <Card title="School invite link" subtitle="Share this link with your dancers — their school will be pre-filled automatically.">
                  <div style={{ background: '#F5F5F5', borderRadius: 8, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 13, color: '#2D2E33', fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {inviteLink}
                    </span>
                    <Button size="sm" onClick={() => navigator.clipboard.writeText(inviteLink)} disabled={newDancersBlocked}>Copy</Button>
                  </div>
                </Card>
                <div style={{ height: 16 }} />
                {!newDancersBlocked && <CsvImportCard dancers={dancers} onImported={refetch.dancers} />}
              </>
            );
          })()}

          {activeTab === 'Roster' && (
            <>
              {(missingConsent.length > 0 || notStarted.length > 0 || dancersWithBalance.length > 0) && (
                <OutstandingBanner
                  id="dancers-outstanding"
                  title={`${missingConsent.length + notStarted.length + dancersWithBalance.length} outstanding items`}
                  items={[
                    ...(notStarted.length > 0 ? [{ header: true, fontWeight: 500, label: 'Registration Not Started' }] : []),
                    ...notStarted.map((d) => ({ label: `${d.firstName} ${d.lastName} — registration not started` })),
                    ...(missingConsent.length > 0 ? [{ header: true, fontWeight: 700, label: 'Missing Parent/Guardian Media Consent' }] : []),
                    ...missingConsent.map((d) => ({ label: `${d.firstName} ${d.lastName} — parent consent pending` })),
                    ...(dancersWithBalance.length > 0 ? [{ header: true, fontWeight: 700, label: 'Balance Owing' }] : []),
                    ...dancersWithBalance.map((d) => ({ label: `${d.firstName} ${d.lastName} — balance owing` })),
                  ]}
                />
              )}
              <Card title="Registered dancers">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '10px 0', marginBottom: 6, borderBottom: '1px solid #E5E5E5', fontSize: 13, color: '#666' }}>
                  {!isMobile && (
                    <input
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search dancers…"
                      style={{ fontSize: 13, padding: '5px 8px', border: '1px solid #D5D5D5', borderRadius: 6, flex: 1, minWidth: 10 }}
                    />
                  )}
                  <select value={filterReg} onChange={e => setFilterReg(e.target.value)}
                    style={{ fontSize: 13, padding: '5px 8px', border: '1px solid #D5D5D5', borderRadius: 6 }}>
                    <option value="all">All dancers</option>
                    <option value="not-started">Not started</option>
                    <option value="parent-pending">Parent consent pending</option>
                    <option value="complete">Complete</option>
                  </select>

                  <select value={filterPay} onChange={e => setFilterPay(e.target.value)}
                    style={{ fontSize: 13, padding: '5px 8px', border: '1px solid #D5D5D5', borderRadius: 6 }}>
                    <option value="all">All payments</option>
                    <option value="fully-paid">Fully paid</option>
                    <option value="participation-paid">Partially paid</option>
                    <option value="not-paid">Not paid</option>
                  </select>
                </div>

                {dancers.length === 0 ? (
                  <EmptyState
                    icon="💃"
                    title="No dancers yet"
                    description="Share your invite link with dancers to get your roster started."
                    action={<Button variant="primary" onClick={() => setActiveTab('Invite link')}>Go to invite link</Button>}
                  />
                ) : filteredDancers.length === 0 ? (
                  <EmptyState
                    icon="🔍"
                    title="No dancers match these filters"
                    description="Try adjusting or clearing your filters."
                    action={<Button onClick={() => { setFilterReg('all'); setFilterPay('all'); }}>Clear filters</Button>}
                  />
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th align="left" onClick={() => handleSort('name')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            Dancer {sortCol === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                          </th>
                          <th onClick={() => handleSort('registration')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            Registration {sortCol === 'registration' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                          </th>
                          <th onClick={() => handleSort('payment')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            Payment received {sortCol === 'payment' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                          </th>
                          <th align="right" onClick={() => handleSort('balance')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            Balance owing {sortCol === 'balance' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDancers.map((d) => {
                          const balance = balanceOwingForDancer(d, entries, feeConfig, entryFeePayments);
                          const status = paymentStatusForDancer(d, entries, feeConfig, entryFeePayments);
                          const regBadge = !d.studentSectionComplete ? { variant: 'danger', label: 'Not started' } : !d.parentSectionComplete ? { variant: 'warning', label: 'Parent pending' } : { variant: 'success', label: 'Complete' };
                          return (
                            <tr key={d.id} onClick={() => setSelectedDancer(d)} style={{ cursor: 'pointer' }}>
                              <Td>{d.firstName} {d.lastName}<br /><span style={{ color: '#666', fontSize: 11 }}>Gr. {d.grade} - {d.experienceLevel}</span></Td>
                              <Td align="center"><Badge variant={regBadge.variant}>{regBadge.label}</Badge></Td>
                              <Td align="center"><Badge variant={PAYMENT_BADGE[status].variant}>{PAYMENT_BADGE[status].label}</Badge></Td>
                              <Td align="right" bold color={balance === 0 ? '#0F6E56' : undefined}>{formatCurrency(balance)}</Td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </>
          )
          }

          {
            activeTab === 'Groups' && (
              groups.length === 0 ? (
                <Card>
                  <EmptyState
                    icon="👥"
                    title="No groups yet"
                    description="Create a group to quickly add a whole set of dancers to an entry at once."
                    action={<Button variant="primary" size="sm" onClick={startNewGroup}>+ Create a group</Button>}
                  />
                </Card>
              ) : (
                <>
                  {groups.map((g) => {
                    const dancerNames = g.dancerIds
                      .map((id) => dancers.find((dd) => dd.id === id))
                      .filter(Boolean)
                      .map((d) => `${d.firstName} ${d.lastName}`)
                      .join(', ');
                    return (
                      <div key={g.id} onClick={() => setEditingGroup(g)} style={{ cursor: 'pointer' }}>
                        <Card title={g.name} action={<span style={{ fontSize: 12, color: '#666' }}>{g.dancerIds.length} dancers</span>}>
                          <span style={{ fontSize: 13, color: '#666' }}>{dancerNames}</span>
                        </Card>
                      </div>
                    );
                  })}
                  <Button variant="primary" size="sm" onClick={startNewGroup}>+ Create a group</Button>
                </>
              )
            )
          }
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {editingGroup && (
          <GroupEditor
            key="group-editor"
            group={editingGroup}
            dancers={dancers}
            onCancel={() => setEditingGroup(null)}
            onSave={handleSaveGroup}
            onDelete={handleDeleteGroup}
          />
        )}
      </AnimatePresence>
    </div >
  );
}

function GroupEditor({ group, dancers, onCancel, onSave, onDelete }) {
  const [name, setName] = useState(group.name || '');
  const [dancerIds, setDancerIds] = useState(group.dancerIds || []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const dancerNames = (ids) => dancers.filter((d) => ids.includes(d.id)).map((d) => `${d.firstName} ${d.lastName}`).join(', ');

  const toggleId = (id) => {
    setDancerIds((prev) => {
      const set = new Set(prev);
      set.has(id) ? set.delete(id) : set.add(id);
      return Array.from(set);
    });
  };

  // ids is the currently-visible (search-filtered) option list from the
  // Picker; shouldClear tells us whether it's already fully selected.
  const selectAllIds = (ids, shouldClear) => {
    setDancerIds((prev) => {
      const set = new Set(prev);
      ids.forEach((id) => (shouldClear ? set.delete(id) : set.add(id)));
      return Array.from(set);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ id: group.id, name: name.trim(), dancerIds });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onCancel} panelStyle={{ width: 480, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontSize: 16, fontWeight: 500 }}>{group.id ? 'Edit group' : 'Create a group'}</span>
        <button onClick={onCancel} aria-label="Close" style={{ border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: '#666' }}>✕</button>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13, color: '#666', marginBottom: 5, display: 'block' }}>Group name</label>
        <input
          autoFocus
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #D5D5D5', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Senior Contemporary"
        />
      </div>

      <div style={{ marginBottom: 18 }}>
        <label style={{ fontSize: 13, color: '#666', marginBottom: 5, display: 'block' }}>Dancers</label>
        <Picker
          open={pickerOpen}
          onToggle={() => setPickerOpen((o) => !o)}
          value={dancerNames(dancerIds)}
          placeholder="Select dancers from roster..."
          options={dancers}
          selectedIds={dancerIds}
          onToggleId={toggleId}
          onSelectAll={selectAllIds}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button variant="primary" onClick={handleSave} disabled={!name.trim() || dancerIds.length === 0 || saving}>
          {saving ? 'Saving…' : '✓ Save group'}
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
        {group.id && (
          <div style={{ marginLeft: 'auto' }}>
            <Button variant="danger" onClick={onDelete}>🗑 Delete group</Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

const CSV_ROW_STATUS = {
  ready: { variant: 'success', label: 'Ready' },
  'missing-field': { variant: 'danger', label: 'Missing name' },
  'invalid-email': { variant: 'danger', label: 'Invalid email' },
  'duplicate-in-file': { variant: 'warning', label: 'Duplicate in file' },
  'already-on-roster': { variant: 'warning', label: 'Already on roster' },
};

function CsvImportCard({ dancers, onImported }) {
  const fileInputRef = useRef(null);
  const [parsedRows, setParsedRows] = useState(null); // [{ firstName, lastName, email, status }]
  const [parseError, setParseError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // { added, skipped, importedParticipantIds }
  const [sendingInvites, setSendingInvites] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file after fixing it
    if (!file) return;

    setImportResult(null);
    setInviteResult(null);
    setParseError(null);
    setParsedRows(null);

    const text = await file.text();
    const { error, rows } = parseStudentCsv(text);
    if (error) { setParseError(error); return; }
    if (rows.length === 0) { setParseError('No student rows found in that file.'); return; }

    const existingEmails = new Set(dancers.map((d) => d.email.trim().toLowerCase()).filter(Boolean));
    const seen = new Set();
    const withStatus = rows.map((r) => {
      const email = r.email.toLowerCase();
      let status;
      if (!r.firstName || !r.lastName) status = 'missing-field';
      else if (!/\S+@\S+\.\S+/.test(r.email)) status = 'invalid-email';
      else if (existingEmails.has(email)) status = 'already-on-roster';
      else if (seen.has(email)) status = 'duplicate-in-file';
      else { status = 'ready'; seen.add(email); }
      return { ...r, status };
    });
    setParsedRows(withStatus);
  };

  const readyRows = (parsedRows || []).filter((r) => r.status === 'ready');

  const handleImport = async () => {
    setImporting(true);
    try {
      const result = await sheetsClient.bulkImportDancers(
        readyRows.map(({ firstName, lastName, email }) => ({ firstName, lastName, email }))
      );
      setImportResult(result);
      setParsedRows(null);
      await onImported();
    } catch (err) {
      alert(err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleSendInvites = async () => {
    setSendingInvites(true);
    try {
      const result = await sheetsClient.sendReminders(importResult.importedParticipantIds);
      setInviteResult(result);
    } catch (err) {
      alert(err.message);
    } finally {
      setSendingInvites(false);
    }
  };

  return (
    <Card title="Upload a student list" subtitle="Add students to your roster from a CSV — they'll still need to finish the rest of their registration.">
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
        <Button size="sm" onClick={downloadCsvTemplate}>Download CSV template</Button>
        <Button size="sm" onClick={() => fileInputRef.current?.click()}>Choose CSV file…</Button>
        <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFile} style={{ display: 'none' }} />
      </div>

      {parseError && <p style={{ fontSize: 13, color: '#C0392B', marginBottom: 10 }}>{parseError}</p>}

      {parsedRows && (
        <>
          <div style={{ overflowX: 'auto', marginBottom: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr><Th>Name</Th><Th>Email</Th><Th>Status</Th></tr>
              </thead>
              <tbody>
                {parsedRows.map((r, i) => (
                  <tr key={i}>
                    <Td>{r.firstName} {r.lastName}</Td>
                    <Td>{r.email}</Td>
                    <Td><Badge variant={CSV_ROW_STATUS[r.status].variant}>{CSV_ROW_STATUS[r.status].label}</Badge></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button variant="primary" size="sm" onClick={handleImport} disabled={readyRows.length === 0 || importing}>
            {importing ? 'Adding…' : `Add ${readyRows.length} student${readyRows.length === 1 ? '' : 's'} to roster`}
          </Button>
        </>
      )}

      {importResult && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #E5E5E5' }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
            Added {importResult.added} student{importResult.added === 1 ? '' : 's'} to the roster.
            {importResult.skipped.length > 0 && ` ${importResult.skipped.length} skipped.`}
          </p>
          {importResult.added > 0 && !inviteResult && (
            <Button size="sm" onClick={handleSendInvites} disabled={sendingInvites}>
              {sendingInvites ? 'Sending…' : 'Email registration link to these students now'}
            </Button>
          )}
          {inviteResult && (
            <p style={{ fontSize: 13, color: '#666' }}>
              Sent {inviteResult.studentRemindersSent} registration email{inviteResult.studentRemindersSent === 1 ? '' : 's'}.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

function DancerDetail({ dancer, dancers, entries, feeConfig, entryFeePayments, onBack, onSave, onUpdateParentEmail, onResendParentEmail, onDelete, onReplace }) {
  const { event, locks } = useTeacherData();
  const eventName = event?.[0]?.eventName || 'OSS Dancefest';
  const isMobile = useMediaQuery('(max-width:768px)');
  const [form, setForm] = useState({ ...dancer });
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const dancerEntries = entriesForDancer(dancer.id, entries);
  const entryFeeTotal = entryFeeTotalForDancer(dancer.id, entries, feeConfig.ENTRY_RATES);

  // Real dollar amounts, not the legacy orgParticipationFeePaid/
  // orgEntryFeePaid booleans (the latter is never actually set anywhere on
  // the backend, so it always read as "pending" regardless of reality).
  const participationPaid = dancer.participationFeeAmountPaid || 0;
  const entryFeePaidTotal = paidForParticipant(dancer.id, entryFeePayments);
  const credit = creditFor(dancer.id, entryFeePayments);
  const balance = Math.max(0, feeConfig.PARTICIPATION_FEE - participationPaid) + Math.max(0, entryFeeTotal - entryFeePaidTotal);

  const handleCancel = () => { setForm({ ...dancer }); setEditing(false); };

  // Warns about anything deleting this participant won't clean up on its
  // own — see the backend route's comment for why entries/payments aren't
  // touched automatically — so the teacher isn't surprised later.
  const handleDelete = async () => {
    const warnings = [];
    if (dancerEntries.length > 0) {
      warnings.push(`They're currently in ${dancerEntries.length} ${dancerEntries.length === 1 ? 'entry' : 'entries'} (${dancerEntries.map(e => e.title).join(', ')}) — deleting them will not remove them from those entries.`);
    }
    if (participationPaid > 0 || entryFeePaidTotal > 0) {
      warnings.push("They have recorded fee payments — deleting them will not remove that payment history.");
    }
    const message = [`Delete ${dancer.firstName} ${dancer.lastName}? This cannot be undone.`, ...warnings].join('\n\n');
    if (!window.confirm(message)) return;
    setDeleting(true);
    try {
      await onDelete();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const otherDancers = (dancers || []).filter((d) => d.id !== dancer.id);

  return (
    <div style={{ padding: isMobile ? 12 : 20 }}>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <Button size="sm" onClick={onBack}>← Back to roster</Button>
        <div style={{ display: 'flex', gap: 8 }}>
          {otherDancers.length > 0 && (
            <Button size="sm" onClick={() => setReplacing(true)} disabled={deleting}>
              🔁 Replace with another dancer
            </Button>
          )}
          <Button size="sm" variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : '🗑 Delete participant'}
          </Button>
        </div>
      </div>

      {replacing && (
        <ReplaceDancerModal
          dancer={dancer}
          dancerEntries={dancerEntries}
          otherDancers={otherDancers}
          participationPaid={participationPaid}
          onClose={() => setReplacing(false)}
          onConfirm={async (newParticipantId, transferEntryTitles) => {
            await onReplace(newParticipantId, transferEntryTitles);
          }}
        />
      )}

      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 20, alignItems: isMobile ? 'stretch' : 'flex-start' }}>

        {/* Left column — dancer details */}
        <div style={{ width: isMobile ? '100%' : 420, flexShrink: 0, minWidth: 0, boxSizing: 'border-box' }}>
          <Card
            title={`${dancer.firstName} ${dancer.lastName}`}
            action={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Badge variant={!dancer.studentSectionComplete ? 'danger' : !dancer.parentSectionComplete ? 'warning' : 'success'}>
                  {!dancer.studentSectionComplete ? 'Not started' : !dancer.parentSectionComplete ? 'Parent pending' : 'Complete'}
                </Badge>
                {!editing && <Button size="sm" onClick={() => setEditing(true)}>Edit</Button>}
              </div>
            }
            subtitle={editing ? 'Edit fields below and save your changes.' : 'Submitted via registration form.'}
          >
            {editing ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', columnGap: 12, rowGap: 16 }}>
                  <Field label="First name" value={form.firstName} onChange={(v) => setForm({ ...form, firstName: v })} />
                  <Field label="Last name" value={form.lastName} onChange={(v) => setForm({ ...form, lastName: v })} />
                  <SelectField label="Grade" value={form.grade} onChange={(v) => setForm({ ...form, grade: v })} options={['9', '10', '11', '12']} />
                  <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                  <SelectField label="Role" value={form.role} onChange={(v) => setForm({ ...form, role: v, firstTimeChoreographer: v === 'Dancer' ? null : form.firstTimeChoreographer })} options={['Dancer', 'Choreographer', 'Dancer and Choreographer']} />
                  {locks?.levelsLocked ? (
                    <ReadonlyField label="Experience level" value={form.experienceLevel} note="🔒 Locked — participant registration is closed" />
                  ) : (
                    <SelectField label="Experience level" value={form.experienceLevel} onChange={(v) => setForm({ ...form, experienceLevel: v })} options={['Novice', 'Intermediate 1', 'Intermediate 2', 'Advanced 1', 'Advanced 2']} />
                  )}
                  <SelectField label="T-shirt size" value={form.tshirtSize} onChange={(v) => setForm({ ...form, tshirtSize: v })} options={['Unisex S', 'Unisex M', 'Unisex L', 'Unisex XL', 'Unisex 2XL']} />
                  <SelectField label="Graduating?" value={form.graduating ? 'Yes' : 'No'} onChange={(v) => setForm({ ...form, graduating: v === 'Yes' })} options={['Yes', 'No']} />
                  <SelectField label="First time at Dancefest?" value={form.firstTimeDancefest ? 'Yes' : 'No'} onChange={(v) => setForm({ ...form, firstTimeDancefest: v === 'Yes' })} options={['Yes', 'No']} />
                  {form.role && form.role !== 'Dancer' && (
                    <SelectField label="First time choreographer?" value={form.firstTimeChoreographer === null || form.firstTimeChoreographer === undefined ? '' : form.firstTimeChoreographer ? 'Yes' : 'No'} onChange={(v) => setForm({ ...form, firstTimeChoreographer: v === 'Yes' })} options={['Yes', 'No']} />
                  )}
                  <Field label="Dietary / allergies" value={form.dietary} onChange={(v) => setForm({ ...form, dietary: v })} />
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <Button variant="primary" onClick={() => onSave(form)}>✓ Save changes</Button>
                  <Button onClick={handleCancel}>Cancel</Button>
                </div>
              </>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', columnGap: 12, rowGap: 12 }}>
                <ReadonlyField label="First name" value={dancer.firstName} />
                <ReadonlyField label="Last name" value={dancer.lastName} />
                <ReadonlyField label="Grade" value={dancer.grade} />
                <ReadonlyField label="Email" value={dancer.email} />
                <ReadonlyField label="Role" value={dancer.role} />
                <ReadonlyField label="Experience level" value={dancer.experienceLevel} />
                <ReadonlyField label="T-shirt size" value={dancer.tshirtSize} />
                <ReadonlyField label="Graduating?" value={dancer.graduating ? 'Yes' : 'No'} />
                <ReadonlyField label="First time at Dancefest?" value={dancer.firstTimeDancefest ? 'Yes' : 'No'} />
                {dancer.role && dancer.role !== 'Dancer' && (
                  <ReadonlyField label="First time choreographer?" value={dancer.firstTimeChoreographer === null ? '—' : dancer.firstTimeChoreographer ? 'Yes' : 'No'} />
                )}
                <ReadonlyField label="Dietary / allergies" value={dancer.dietary} />
              </div>
            )}
          </Card>
        </div>

        {/* Right column — parent info + payment */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <ParentInfoCard
            dancer={dancer}
            onUpdateParentEmail={onUpdateParentEmail}
            onResendParentEmail={onResendParentEmail}
          />

          <Card title="Entries & payment breakdown" subtitle={`In ${dancerEntries.length} ${dancerEntries.length === 1 ? 'entry' : 'entries'} for ${eventName}`}>
            <LineRow
              label="Participation fee"
              value={participationPaid > 0 && participationPaid < feeConfig.PARTICIPATION_FEE
                ? `${formatCurrency(participationPaid)} of ${formatCurrency(feeConfig.PARTICIPATION_FEE)} paid`
                : formatCurrency(feeConfig.PARTICIPATION_FEE)}
              struck={feeConfig.PARTICIPATION_FEE > 0 && participationPaid >= feeConfig.PARTICIPATION_FEE}
            />
            <LineRow
              label="Entry fees"
              value={entryFeePaidTotal > 0 && entryFeePaidTotal < entryFeeTotal
                ? `${formatCurrency(entryFeePaidTotal)} of ${formatCurrency(entryFeeTotal)} paid`
                : formatCurrency(entryFeeTotal)}
              struck={entryFeeTotal > 0 && entryFeePaidTotal >= entryFeeTotal}
            />
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: isMobile ? '100%' : '90%', borderCollapse: 'collapse', marginLeft: isMobile ? 0 : 30 }}>
                <thead><tr><Th>Entry</Th><Th>Size</Th><Th align="right">Fee</Th><Th align="right">Paid</Th></tr></thead>
                <tbody>
                  {dancerEntries.length === 0 ? (
                    <tr><Td colSpan={4} style={{ color: '#666' }}>Not currently in any entries</Td></tr>
                  ) : (
                    dancerEntries.map((e) => (
                      <tr key={e.id}>
                        <Td>{e.title}</Td>
                        <Td>{e.size}</Td>
                        <Td align="right">{formatCurrency(entryRateFor(e, feeConfig.ENTRY_RATES))}</Td>
                        <Td align="right">{formatCurrency(paidForEntryAndParticipant(e.title, dancer.id, entryFeePayments))}</Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, marginTop: 6, borderTop: '1px solid #D5D5D5', fontSize: 15, fontWeight: 500 }}>
              <span>Balance owing</span>
              <span style={{ color: balance === 0 ? '#0F6E56' : undefined }}>{balance === 0 ? 'Paid in full' : formatCurrency(balance)}</span>
            </div>
            <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Badge variant={feeConfig.PARTICIPATION_FEE > 0 && participationPaid >= feeConfig.PARTICIPATION_FEE ? 'success' : 'warning'}>
                Participation: {feeConfig.PARTICIPATION_FEE > 0 && participationPaid >= feeConfig.PARTICIPATION_FEE ? 'paid' : 'pending'}
              </Badge>
              <Badge variant={entryFeeTotal > 0 && entryFeePaidTotal >= entryFeeTotal ? 'success' : 'warning'}>
                Entry fees: {entryFeeTotal > 0 && entryFeePaidTotal >= entryFeeTotal ? 'paid' : 'pending'}
              </Badge>
              {credit > 0 && <Badge variant="success">Credit: {formatCurrency(credit)}</Badge>}
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}

// Lets a teacher transfer this dancer's participation fee, entries, and
// entry-fee history to another already-registered dancer, instead of
// deleting them and losing track of what they paid. Per entry, not
// all-or-nothing — the replacement might only be taking over one of several
// entries; anything left behind turns into a school credit (see the backend
// route's comment for the full transfer/credit rules).
function ReplaceDancerModal({ dancer, dancerEntries, otherDancers, participationPaid, onClose, onConfirm }) {
  const [newId, setNewId] = useState('');
  const [transferTitles, setTransferTitles] = useState(() => new Set(dancerEntries.map((e) => e.title)));
  const [submitting, setSubmitting] = useState(false);

  const toggleTitle = (title) => setTransferTitles((prev) => {
    const next = new Set(prev);
    next.has(title) ? next.delete(title) : next.add(title);
    return next;
  });

  const newDancer = otherDancers.find((d) => d.id === newId);

  const handleConfirm = async () => {
    if (!newId || !newDancer) return;
    const leftBehindCount = dancerEntries.length - transferTitles.size;
    const message = [
      `Replace ${dancer.firstName} ${dancer.lastName} with ${newDancer.firstName} ${newDancer.lastName}?`,
      participationPaid > 0 ? `Their participation fee (${formatCurrency(participationPaid)}) will transfer to ${newDancer.firstName}.` : null,
      transferTitles.size > 0 ? `${newDancer.firstName} will take over ${transferTitles.size} ${transferTitles.size === 1 ? 'entry' : 'entries'}: ${Array.from(transferTitles).join(', ')}.` : null,
      leftBehindCount > 0 ? `${dancer.firstName} will be removed from the other ${leftBehindCount} ${leftBehindCount === 1 ? 'entry' : 'entries'} — anything already paid toward ${leftBehindCount === 1 ? 'it' : 'them'} becomes a school credit.` : null,
      'This cannot be undone.',
    ].filter(Boolean).join('\n\n');
    if (!window.confirm(message)) return;
    setSubmitting(true);
    try {
      await onConfirm(newId, Array.from(transferTitles));
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} panelStyle={{ width: 480, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontSize: 16, fontWeight: 500 }}>Replace {dancer.firstName} {dancer.lastName}</span>
        <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: '#666' }}>✕</button>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13, color: '#666', marginBottom: 5, display: 'block' }}>Replace with</label>
        <select
          value={newId}
          onChange={(e) => setNewId(e.target.value)}
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #D5D5D5', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', background: '#fff' }}
        >
          <option value="">— Select a dancer —</option>
          {otherDancers.map((d) => <option key={d.id} value={d.id}>{d.firstName} {d.lastName}</option>)}
        </select>
      </div>

      {dancerEntries.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <label style={{ fontSize: 13, color: '#666', marginBottom: 5, display: 'block' }}>
            Which entries should transfer? Uncheck any {dancer.firstName} should just be removed from instead — anything already paid toward those becomes a school credit.
          </label>
          {dancerEntries.map((e) => {
            const roles = [
              e.dancerIds.includes(dancer.id) ? 'Dancer' : null,
              e.choreographerIds.includes(dancer.id) ? 'Choreographer' : null,
            ].filter(Boolean).join(' & ');
            return (
              <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={transferTitles.has(e.title)} onChange={() => toggleTitle(e.title)} />
                <span style={{ flex: 1 }}>{e.title}</span>
                <span style={{ color: '#666', fontSize: 12 }}>{roles}</span>
              </label>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button variant="primary" onClick={handleConfirm} disabled={!newId || submitting}>
          {submitting ? 'Replacing…' : '✓ Replace dancer'}
        </Button>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
      </div>
    </Modal>
  );
}

function ParentInfoCard({ dancer, onUpdateParentEmail, onResendParentEmail }) {
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState(dancer.parentGuardianEmail || '');
  const [savingEmail, setSavingEmail] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  const canResend = dancer.studentSectionComplete && !!dancer.parentGuardianEmail && /\S+@\S+\.\S+/.test(dancer.parentGuardianEmail);

  const handleSaveEmail = async () => {
    setSavingEmail(true);
    await onUpdateParentEmail(dancer.id, emailDraft.trim());
    setSavingEmail(false);
    setEditingEmail(false);
  };

  const handleResend = async () => {
    setResending(true);
    setResendSent(false);
    await onResendParentEmail(dancer.id);
    setResending(false);
    setResendSent(true);
  };

  return (
    <Card
      title="Parent Info & Media Release"
      action={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Badge variant={dancer.parentSectionComplete ? 'success' : 'warning'}>
            {dancer.parentSectionComplete ? 'Complete' : 'Pending'}
          </Badge>

          {!dancer.parentSectionComplete && (
            <button
              onClick={handleResend}
              disabled={!canResend || resending}
              title={!canResend
                ? (!dancer.studentSectionComplete ? 'Student hasn\u2019t registered yet' : 'Add a parent email first')
                : undefined}
              style={{
                fontSize: 12,
                color: !canResend ? '#B0B0B0' : '#2D2E33',
                background: 'none',
                border: 'none',
                cursor: !canResend ? 'default' : 'pointer',
                padding: 0,
                textDecoration: canResend ? 'underline' : 'none',
              }}
            >
              {resending ? 'Sending…' : resendSent ? 'Sent ✓' : 'Resend link'}
            </button>
          )}
        </div>
      }
    >
      {dancer.parentSectionComplete ? (
        <>
          <DRow label="Parent/guardian" value={dancer.parentGuardianName} />
          <ParentEmailRow
            email={dancer.parentGuardianEmail}
            editing={editingEmail}
            draft={emailDraft}
            saving={savingEmail}
            onEditStart={() => { setEmailDraft(dancer.parentGuardianEmail || ''); setEditingEmail(true); }}
            onChange={setEmailDraft}
            onSave={handleSaveEmail}
            onCancel={() => setEditingEmail(false)}
          />
          <DRow label="Signed" value={`${dancer.mediaReleaseSignedName} on ${dancer.mediaReleaseSignedDate}`} />
        </>
      ) : (
        <>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
            Awaiting completion by: {dancer.parentGuardianName || 'parent/guardian'}.
          </p>
          <ParentEmailRow
            email={dancer.parentGuardianEmail}
            editing={editingEmail}
            draft={emailDraft}
            saving={savingEmail}
            onEditStart={() => { setEmailDraft(dancer.parentGuardianEmail || ''); setEditingEmail(true); }}
            onChange={setEmailDraft}
            onSave={handleSaveEmail}
            onCancel={() => setEditingEmail(false)}
          />
        </>
      )}
    </Card>
  );
}

function ParentEmailRow({ email, editing, draft, saving, onEditStart, onChange, onSave, onCancel }) {
  if (editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid #E5E5E5' }}>
        <span style={{ fontSize: 13, color: '#666', width: 90, flexShrink: 0 }}>Email</span>
        <input
          autoFocus
          type="email"
          value={draft}
          onChange={(e) => onChange(e.target.value)}
          style={{ flex: 1, fontSize: 13, padding: '5px 8px', border: '1px solid #D5D5D5', borderRadius: 6, fontFamily: 'inherit' }}
        />
        <Button size="sm" variant="primary" onClick={onSave} disabled={saving}>{saving ? '…' : 'Save'}</Button>
        <Button size="sm" onClick={onCancel}>Cancel</Button>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #E5E5E5', fontSize: 13 }}>
      <span style={{ color: '#666' }}>Email</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 500, color: email ? '#1A1A1A' : '#C0392B' }}>{email || 'No email on file'}</span>
        <button onClick={onEditStart} style={{ fontSize: 12, color: '#2D2E33', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Edit</button>
      </span>
    </div>
  );
}


function ReadonlyField({ label, value, note }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#999', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 500, color: '#1A1A1A' }}>{value || '—'}</div>
      {note && <div style={{ fontSize: 11, color: '#666', marginTop: 3 }}>{note}</div>}
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label style={{ fontSize: 13, color: '#666', marginBottom: 5, display: 'block' }}>{label}</label>
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', padding: '8px 12px', border: '1px solid #D5D5D5', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', background: '#fff' }}>
        <option value="">— Select —</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div>
      <label style={{ fontSize: 13, color: '#666', marginBottom: 5, display: 'block' }}>{label}</label>
      <input style={{ width: '100%', padding: '8px 12px', border: '1px solid #D5D5D5', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
function DRow({ label, value }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #E5E5E5', fontSize: 13 }}><span style={{ color: '#666' }}>{label}</span><span style={{ fontWeight: 500 }}>{value}</span></div>;
}
function LineRow({ label, value, struck }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #E5E5E5', fontSize: 13, color: struck ? '#999' : '#1A1A1A' }}>
      <span>{label}{struck ? ' (paid)' : ''}</span>
      <span style={{ textDecoration: struck ? 'line-through' : 'none' }}>{value}</span>
    </div>
  );
}
function Th({ children, align }) { return <th style={{ textAlign: align || 'left', fontSize: 12, color: '#666', fontWeight: 500, padding: '6px', borderBottom: '1px solid #E5E5E5' }}>{children}</th>; }
function Td({ children, align, bold, color, colSpan }) { return <td colSpan={colSpan} style={{ fontSize: 13, padding: '8px 6px', borderBottom: '1px solid #E5E5E5', textAlign: align || 'left', fontWeight: bold ? 500 : 400, color }}>{children}</td>; }
